import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import { OUTBOX_LABEL, OUTBOX_PATH, isOutboxPath } from './route';
import { useOutboxCounts } from './useOutbox';

/**
 * The Outbox row in the folder tree, drawn immediately under Drafts.
 *
 * ## Why here and not somewhere cleverer
 *
 * It is not a server folder and it never will be — the messages in it exist on
 * this device and nowhere else. But "where a folder would be" is the only place
 * a user will look for a message they pressed Send on, and a queue nobody can
 * find is a queue nobody trusts. Drafts is the right neighbour: Drafts is
 * "written, not sent", the Outbox is "sent, not gone", and between them they
 * are every place a message can be while it is still the user's problem.
 *
 * ## Why it disappears when it is empty
 *
 * An Outbox with nothing in it is a folder that can never be opened to any
 * purpose, and a permanent empty row trains people to stop reading the sidebar.
 * It appears the moment something is queued — which is exactly when it matters
 * — and the banner points at it as well.
 *
 * The count is the whole queue, not this mailbox's share of it: the deployment
 * runs one active mailbox at a time (`app/accounts.tsx`), and a row that showed
 * 2 while the Outbox held 3 would be the dishonesty this feature exists to
 * avoid. The view itself is scoped per account and says so when it filters.
 */
export function OutboxFolderEntry({ depth = 0 }: { depth?: number }) {
  // `FolderTree` is also rendered bare, outside any router, by its own
  // component tests and by anywhere a plain list of folders is wanted -- the
  // same reason it treats the folder context as optional. A row that called
  // `useNavigate` there would throw the whole tree down.
  const inRouter = useInRouterContext();
  const { waiting, failed } = useOutboxCounts();
  if (!inRouter || waiting + failed === 0) return null;
  return <OutboxRow depth={depth} waiting={waiting} failed={failed} />;
}

function OutboxRow({
  depth,
  waiting,
  failed,
}: {
  depth: number;
  waiting: number;
  failed: number;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const total = waiting + failed;

  const selected = isOutboxPath(location.pathname);
  const label =
    failed > 0
      ? `${OUTBOX_LABEL}, ${total} waiting, ${failed} couldn't be sent`
      : `${OUTBOX_LABEL}, ${total} waiting`;

  return (
    <div role="treeitem" aria-level={depth + 1} aria-selected={selected} className="outbox-entry">
      <div className={selected ? 'folder-tree__row is-active' : 'folder-tree__row'}>
        <span className="folder-tree__twisty folder-tree__twisty--placeholder" />
        <button
          type="button"
          className="folder-tree__main"
          aria-current={selected ? 'page' : undefined}
          aria-label={label}
          onClick={() => navigate(OUTBOX_PATH)}
        >
          <Icon name="send" size={16} />
          <span className="folder-tree__label">{OUTBOX_LABEL}</span>
          <span
            className={failed > 0 ? 'badge badge--danger' : selected ? 'badge badge--accent' : 'badge'}
            aria-hidden="true"
          >
            {total}
          </span>
        </button>
      </div>
    </div>
  );
}
